import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, FolderOpen, Upload, BarChart3, FileText, Settings, Building2, FileCheck, Shield, TestTube } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "./ui/sidebar";
import { useQuickStats } from '@/hooks/useQuickStats';
import { useUserRole } from '@/hooks/useUserRole';
interface Organization {
  id: string;
  name: string;
  login?: string;
  avatar_url?: string;
  [key: string]: unknown;
}
interface AppSidebarProps {
  organization: Organization | null;
}
export function AppSidebar({
  organization
}: AppSidebarProps) {
  const {
    state
  } = useSidebar();
  const location = useLocation();
  const {
    t
  } = useTranslation('common');
  const { role } = useUserRole();
  
  // Get real-time statistics from database
  const { stats, loading: statsLoading } = useQuickStats();

  // Check if Testlab should be visible (bara för system_admin)
  const isTestlabVisible = role === 'system_admin';

  // Navigation items for the revision app
  const navigationItems = [{
    title: t('navigation.dashboard'),
    url: "/",
    icon: LayoutDashboard,
    description: t('sidebar.description.dashboard')
  }, {
    title: t('navigation.cases'),
    url: "/cases",
    icon: FolderOpen,
    description: t('sidebar.description.cases')
  }, {
    title: t('navigation.queue'),
    url: "/queue",
    icon: BarChart3,
    description: t('sidebar.description.queue')
  }, {
    title: "Statistik",
    url: "/statistics",
    icon: BarChart3,
    description: "Visa grafer och trender över tid"
  }, {
    title: t('navigation.team'),
    url: "/team",
    icon: Building2,
    description: t('sidebar.description.team')
  }, {
    title: t('navigation.billing'),
    url: "/billing",
    icon: FileText,
    description: t('sidebar.description.billing')
  }, {
    title: t('navigation.settings'),
    url: "/settings",
    icon: Settings,
    description: t('sidebar.description.settings')
  }];

  // Development navigation items (conditional)
  const devNavigationItems = [];
  if (isTestlabVisible) {
    devNavigationItems.push({
      title: t('testlab.title'),
      url: "/testlab",
      icon: TestTube,
      description: "Intern testmiljö"
    });
  }
  const isCollapsed = state === "collapsed";

  // Helper to check if current route matches
  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };
  return <Sidebar className={isCollapsed ? "w-16" : "w-64"}>
      <SidebarContent className="border-r border-gray-200 bg-white">
        {/* App branding */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <img src="/lovable-uploads/f1bb597c-c583-4985-ae82-477955b7b8c0.png" alt="AuditAI" className="h-32 w-auto flex-shrink-0" />
            {!isCollapsed && <div>
                
                
              </div>}
          </div>
        </div>

        {/* Organization info */}
        {!isCollapsed && organization && <div className="p-4 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700 truncate">
                {organization.name}
              </span>
            </div>
          </div>}

        {/* Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-gray-500 font-medium">{t('navigation.dashboard')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationItems.map(item => <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} className={({
                  isActive: linkActive
                }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${linkActive || isActive(item.url) ? "bg-blue-50 text-blue-700 border-l-4 border-blue-600 font-medium" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"}`}>
                      <item.icon className="h-4 w-4 flex-shrink-0" />
                      {!isCollapsed && <div>
                          <span className="text-sm font-medium">{item.title}</span>
                          <p className="text-xs opacity-75">{item.description}</p>
                        </div>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Development Navigation (conditional) */}
        {devNavigationItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-gray-500 font-medium">Utveckling</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {devNavigationItems.map(item => 
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink to={item.url} className={({
                        isActive: linkActive
                      }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${linkActive || isActive(item.url) ? "bg-amber-50 text-amber-700 border-l-4 border-amber-600 font-medium" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"}`}>
                        <item.icon className="h-4 w-4 flex-shrink-0" />
                        {!isCollapsed && <div>
                            <span className="text-sm font-medium">{item.title}</span>
                            <p className="text-xs opacity-75">{item.description}</p>
                          </div>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Quick stats (only show when expanded) */}
        {!isCollapsed && <SidebarGroup>
            <SidebarGroupLabel className="text-gray-500 font-medium">{t('sidebar.quickstats.title')}</SidebarGroupLabel>
            <SidebarGroupContent>
              <div className="px-3 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{t('sidebar.quickstats.cases')}</span>
                  <span className="font-medium text-gray-900">
                    {statsLoading ? '...' : stats.cases}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{t('sidebar.quickstats.documents')}</span>
                  <span className="font-medium text-gray-900">
                    {statsLoading ? '...' : stats.documents}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{t('sidebar.quickstats.analyses')}</span>
                  <span className="font-medium text-gray-900">
                    {statsLoading ? '...' : stats.analyses}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{t('sidebar.quickstats.reports')}</span>
                  <span className="font-medium text-gray-900">
                    {statsLoading ? '...' : stats.reports}
                  </span>
                </div>
              </div>
            </SidebarGroupContent>
          </SidebarGroup>}

        {/* Policies section at bottom */}
        <div className="mt-auto border-t border-gray-100">
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <NavLink to="/policies" className={({
                    isActive: linkActive
                  }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${linkActive || isActive("/policies") ? "bg-blue-50 text-blue-700 border-l-4 border-blue-600 font-medium" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"}`}>
                      <Shield className="h-4 w-4 flex-shrink-0" />
                      {!isCollapsed && <div>
                          <span className="text-sm font-medium">{t('policies.title')}</span>
                          <p className="text-xs opacity-75">{t('sidebar.policies.description')}</p>
                        </div>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </div>

      </SidebarContent>
    </Sidebar>;
}